import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Leaf, Camera, MapPin, Calendar, MagnifyingGlass, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { API, categoryConfig } from "@/config/constants";
import { useAppContext } from "@/context/AppContext";

const ITEMS_PER_PAGE = 9;

const SpeciesGalleryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLightboxUrl } = useAppContext();
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("category");
    if (cat) setActiveCategory(cat);
  }, [location.search]);

  useEffect(() => {
    setPage(1);
  }, [activeCategory]);

  useEffect(() => {
    const fetchSpecies = async () => {
      try {
        const response = await axios.get(`${API}/species-gallery`);
        setSpecies(response.data);
      } catch (error) { toast.error("Failed to load species gallery"); }
      finally { setLoading(false); }
    };
    fetchSpecies();
  }, []);

  const availableCategories = (() => {
    const cats = {};
    species.forEach(sp => { const cat = sp.category || "unknown"; cats[cat] = (cats[cat] || 0) + 1; });
    return Object.entries(cats).sort((a, b) => b[1] - a[1]).map(([key, count]) => ({ key, count }));
  })();

  const wildlifeCategories = ["mammal", "reptile", "amphibian", "fish", "other"];
  const filteredSpecies = activeCategory === "all" ? species : activeCategory === "wildlife" ? species.filter(sp => wildlifeCategories.includes(sp.category)) : species.filter(sp => sp.category === activeCategory);

  return (
    <div className="max-w-7xl mx-auto page-content" data-testid="species-gallery-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">Species Gallery</h1>
          <p className="text-xs sm:text-base text-white/70 mt-1">Your personal field guide — all identified plants & wildlife</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Leaf size={18} weight="duotone" className="text-white" />
          <span className="text-white font-medium">{species.length} species</span>
        </div>
      </div>

      {/* Category Filter Pills */}
      {!loading && species.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
          <button onClick={() => setActiveCategory("all")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${activeCategory === "all" ? "bg-[#55624F] text-white shadow-sm" : "bg-[#EAE7DE] text-[#55624F] hover:bg-[#D2C2B3]"}`} data-testid="filter-all">
            <MagnifyingGlass size={14} />All ({species.length})
          </button>
          {availableCategories.map(({ key, count }) => {
            const config = categoryConfig[key] || categoryConfig.unknown;
            const Icon = config.icon;
            return (
              <button key={key} onClick={() => setActiveCategory(key)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${activeCategory === key ? "text-white shadow-sm" : "bg-[#EAE7DE] text-[#55624F] hover:bg-[#D2C2B3]"}`} style={activeCategory === key ? { backgroundColor: config.color } : {}} data-testid={`filter-${key}`}>
                <Icon size={14} weight="duotone" />{config.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (<Card key={i} className="border-[#D2C2B3]"><Skeleton className="h-40 bg-[#EAE7DE]" /><CardContent className="p-4"><Skeleton className="h-6 w-3/4 mb-2 bg-[#EAE7DE]" /><Skeleton className="h-4 w-1/2 bg-[#EAE7DE]" /></CardContent></Card>))}
        </div>
      ) : species.length === 0 ? (
        <div className="text-center py-16">
          <img src="https://static.prod-images.emergentagent.com/jobs/d79f4e12-49d2-4273-980e-fb3c434a8417/images/063289cc16a6f951001ce66c1071e2228e26c122a5b28e1a275e5260efaf70fd.png" alt="AI Identification" className="w-48 h-48 mx-auto object-contain opacity-80 mb-4" />
          <p className="font-handwritten text-2xl text-[#7C8068] mb-4">No species identified yet!</p>
          <p className="text-[#7C8068] mb-6">Upload photos and use the AI identification feature to build your personal field guide.</p>
          <Button onClick={() => navigate("/entry/new")} className="bg-[#C25A3C] hover:bg-[#A84B32] text-white" data-testid="create-entry-btn"><Camera size={18} className="mr-2" />Create Entry with Photo</Button>
        </div>
      ) : filteredSpecies.length === 0 ? (
        <div className="text-center py-16"><p className="font-handwritten text-2xl text-[#7C8068] mb-2">No species in this category yet</p><p className="text-[#7C8068]">Try identifying more photos or switch to a different filter.</p></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredSpecies.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(sp => {
            const config = categoryConfig[sp.category] || categoryConfig.unknown;
            const CatIcon = config.icon;
            return (
              <Card key={sp.species_name} className="entry-card bg-white border-[#D2C2B3] overflow-hidden shadow-[0_6px_24px_rgba(50,50,50,0.5)]" data-testid={`species-card-${sp.species_name}`}>
                {sp.sightings[0]?.photo_path ? (
                  <div className="aspect-[4/3] overflow-hidden bg-white relative cursor-pointer" onClick={() => setSelectedSpecies(sp)}>
                    <img src={`${API}/photos/${sp.sightings[0].photo_path}`} alt={sp.species_name} className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2"><Badge style={{ backgroundColor: config.color }} className="text-white text-xs flex items-center gap-1"><CatIcon size={12} weight="bold" />{config.label}</Badge></div>
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-white flex items-center justify-center relative cursor-pointer" onClick={() => setSelectedSpecies(sp)}>
                    <CatIcon size={48} weight="duotone" style={{ color: config.color }} className="opacity-40" />
                    <div className="absolute top-2 right-2"><Badge style={{ backgroundColor: config.color }} className="text-white text-xs flex items-center gap-1"><CatIcon size={12} weight="bold" />{config.label}</Badge></div>
                  </div>
                )}
                <CardContent className="p-4 cursor-pointer hover:bg-[#F5F4F0] transition-colors" onClick={() => setSelectedSpecies(sp)}>
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-[#2E332B] mb-2">{sp.species_name}</h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#7C8068]">
                    <span className="flex items-center gap-1"><Camera size={14} />{sp.total_count} sighting{sp.total_count > 1 ? 's' : ''}</span>
                    {sp.sightings[0]?.location && (<span className="flex items-center gap-1 truncate"><MapPin size={14} />{sp.sightings[0].location}</span>)}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          </div>
          {filteredSpecies.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                disabled={page === 1}
                className="bg-[#55624F] border-[#55624F] text-white hover:bg-[#424D3D] disabled:opacity-30"
                data-testid="species-prev-btn"
              >
                <CaretLeft size={18} className="mr-1" />Previous
              </Button>
              <span className="text-white text-sm font-['Manrope']">
                {page} / {Math.ceil(filteredSpecies.length / ITEMS_PER_PAGE)}
              </span>
              <Button
                variant="outline"
                onClick={() => { setPage(p => Math.min(Math.ceil(filteredSpecies.length / ITEMS_PER_PAGE), p + 1)); window.scrollTo(0, 0); }}
                disabled={page >= Math.ceil(filteredSpecies.length / ITEMS_PER_PAGE)}
                className="bg-[#55624F] border-[#55624F] text-white hover:bg-[#424D3D] disabled:opacity-30"
                data-testid="species-next-btn"
              >
                Next<CaretRight size={18} className="ml-1" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Species Detail Dialog */}
      <Dialog open={!!selectedSpecies} onOpenChange={() => setSelectedSpecies(null)}>
        <DialogContent className="bg-white border-[#D2C2B3] max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedSpecies && (() => {
            const detailConfig = categoryConfig[selectedSpecies.category] || categoryConfig.unknown;
            const DetailIcon = detailConfig.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl sm:text-2xl flex flex-wrap items-center gap-2">
                    <span className="break-words">{selectedSpecies.species_name}</span>
                    <Badge style={{ backgroundColor: detailConfig.color }} className="text-white flex items-center gap-1 text-xs"><DetailIcon size={14} weight="bold" />{detailConfig.label}</Badge>
                  </DialogTitle>
                </DialogHeader>
                {selectedSpecies.sightings[0]?.photo_path && (
                  <div className="mt-3">
                    <img src={`${API}/photos/${selectedSpecies.sightings[0].photo_path}`} alt={selectedSpecies.species_name} className="w-full max-h-[40vh] object-contain rounded-lg bg-white" />
                    <button onClick={() => { setSelectedSpecies(null); setLightboxUrl(`${API}/photos/${selectedSpecies.sightings[0].photo_path}`); }} className="inline-flex items-center justify-center w-full mt-2 h-10 px-4 rounded-md border border-[#D2C2B3] text-sm font-medium text-[#55624F] hover:bg-[#EAE7DE] transition-colors" data-testid="detail-photo-enlarge">
                      <MagnifyingGlass size={16} className="mr-2" />View Full Size Photo
                    </button>
                  </div>
                )}
                <div className="mt-4">
                  <h4 className="font-medium text-[#2E332B] mb-3">{selectedSpecies.total_count} Sighting{selectedSpecies.total_count > 1 ? 's' : ''}</h4>
                  <div className="space-y-3">
                    {selectedSpecies.sightings.map((sighting, idx) => (
                      <Card key={idx} className="border-[#D2C2B3] cursor-pointer hover:bg-[#EAE7DE] transition-colors overflow-hidden" onClick={() => { setSelectedSpecies(null); navigate(`/entry/${sighting.entry_id}`); }}>
                        <CardContent className="p-3 flex items-start gap-3">
                          {sighting.photo_path && (<img src={`${API}/photos/${sighting.photo_path}`} alt={selectedSpecies.species_name} className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg shrink-0 cursor-zoom-in hover:ring-2 hover:ring-[#55624F]" onClick={(e) => { e.stopPropagation(); setSelectedSpecies(null); setLightboxUrl(`${API}/photos/${sighting.photo_path}`); }} />)}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#2E332B] truncate">{sighting.entry_title}</p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-[#7C8068]">
                              {sighting.location && (<span className="flex items-center gap-1 truncate max-w-[150px] sm:max-w-none"><MapPin size={12} />{sighting.location}</span>)}
                              <span className="flex items-center gap-1 whitespace-nowrap"><Calendar size={12} />{new Date(sighting.date).toLocaleDateString()}</span>
                            </div>
                            <p className="text-xs text-[#55624F] mt-1">Confidence: {(sighting.confidence * 100).toFixed(0)}%</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {selectedSpecies.sightings[0]?.description && (
                    <div className="mt-4 p-3 bg-[#EAE7DE] rounded-lg"><h4 className="font-medium text-[#2E332B] mb-2">AI Description</h4><p className="text-sm text-[#55624F]">{selectedSpecies.sightings[0].description}</p></div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpeciesGalleryPage;
